import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import {
  fetchShippingProfiles,
  fetchShippingDestinations,
  deleteShippingProfile,
} from '../../redux/actions/shippingProfiles';
import {
  getShippingProfiles,
  getShippingDestinations,
  getShippingProfilesStatus,
  getShippingDestinationsStatus,
} from '../../redux/selectors/shippingProfiles';
import { ShippingProfile } from '../../redux/types';
import ProfileList from './_partials/ProfileList';
import EmptyState from './_partials/EmptyState';

import s from './ManageShippingProfilesModal.scss';

const MAX_PROFILES = 30;

interface Props {
  onRequestClose: () => void;
}

const ManageShippingProfilesModal: React.FC<Props> = ({ onRequestClose }) => {
  const { t } = useTranslation();

  const profiles = useSelector(getShippingProfiles);
  const destinations = useSelector(getShippingDestinations);
  const profilesStatus = useSelector(getShippingProfilesStatus);
  const destinationsStatus = useSelector(getShippingDestinationsStatus);

  const dispatchFetchProfiles = useAction(fetchShippingProfiles);
  const dispatchFetchDestinations = useAction(fetchShippingDestinations);
  const dispatchDeleteProfile = useAction(deleteShippingProfile);
  const dispatchPushModal = useAction(pushModal);

  useEffect(() => {
    if (profilesStatus === 'uninitialized') {
      dispatchFetchProfiles();
    }
    if (destinationsStatus === 'uninitialized' || destinationsStatus === 'error') {
      dispatchFetchDestinations();
    }
  }, [profilesStatus, destinationsStatus, dispatchFetchProfiles, dispatchFetchDestinations]);

  const handleEditProfile = useCallback(
    (profile: ShippingProfile) => {
      dispatchPushModal(ModalType.SHIPPING_PROFILE_EDIT, { params: { profile } });
    },
    [dispatchPushModal]
  );

  const handleAddProfile = useCallback(() => {
    dispatchPushModal(ModalType.SHIPPING_PROFILE_EDIT, { params: {} });
  }, [dispatchPushModal]);

  const handleDeleteProfile = useCallback(
    (profile: ShippingProfile) => {
      if (!profile.shippingProfileid) {
        return;
      }
      dispatchDeleteProfile({ profileId: profile.shippingProfileid });
    },
    [dispatchDeleteProfile]
  );

  const isEmpty = profiles.length === 0;
  const isLoading = profilesStatus === 'fetching' || profilesStatus === 'uninitialized';

  return (
    <Modal
      isOpen
      onRequestClose={onRequestClose}
      className={s['modal']}
      heading={t('manageShippingProfiles.title')}
      showHeaderBorder
      showFooterBorder
      footerRight={[
        <BrandedButton key="done" variant="brand-green" size="large" onClick={onRequestClose}>
          {t('manageShippingProfiles.done')}
        </BrandedButton>,
      ]}
    >
      <div className={s['content']}>
        {isEmpty && !isLoading ? (
          <EmptyState onAddProfile={handleAddProfile} />
        ) : (
          <ProfileList
            profiles={profiles}
            destinations={destinations}
            onEditProfile={handleEditProfile}
            onDeleteProfile={handleDeleteProfile}
            onAddProfile={profiles.length < MAX_PROFILES ? handleAddProfile : undefined}
          />
        )}
      </div>
    </Modal>
  );
};
ManageShippingProfilesModal.displayName = 'ManageShippingProfilesModal';

export default ManageShippingProfilesModal;
